// To see this message, add the following to the `<head>` section in your
// views/layouts/application.html.erb
//

// If using a TypeScript entrypoint file:
//     <%= vite_typescript_tag 'application' %>
//
// If you want to use .jsx or .tsx, add the extension:
//     <%= vite_javascript_tag 'application.jsx' %>

// import '~/stylesheets/application.scss';

// Example: Load Rails libraries in Vite.
//
import '@hotwired/turbo-rails'
Turbo.start()

import { Application } from '@hotwired/stimulus'
import { registerControllers } from 'stimulus-vite-helpers'

const application = Application.start()
const controllers = import.meta.glob('@/controllers/*_controller.js', { eager: true })
registerControllers(application, controllers)

function setMobileClass() {
  const isMobile = window.matchMedia("(max-width: 768px)").matches;
  document.body.classList.toggle("mobile-view", isMobile);
}

// Prevent flash by adding `.oaded` ASAP
function markBodyLoaded() {
  document.body.classList.add("loaded");
}

let lastNavAuthJson = null;

function readNavAuthJson(root) {
  if (!root || !root.querySelector) {
    return null;
  }

  const authDataEl = root.querySelector("#nav-auth-data");
  const json = authDataEl?.textContent?.trim();
  return json || null;
}

function syncNavAuth(root) {
  const json = readNavAuthJson(root);
  if (!json || json === lastNavAuthJson) {
    return;
  }

  let parsedData;
  try {
    parsedData = JSON.parse(json);
  } catch (error) {
    console.error("Error parsing nav auth data:", error);
    return;
  }

  lastNavAuthJson = json;
  window.dispatchEvent(
    new CustomEvent("nav:auth-updated", {
      detail: parsedData,
    })
  );
}

// Run immediately before rendering
document.addEventListener("DOMContentLoaded", () => {
  setMobileClass();
  markBodyLoaded();
});

document.addEventListener("turbo:load", () => {
  setMobileClass();
  markBodyLoaded();
});

document.addEventListener("turbo:before-render", (event) => {
  // Tear down the outgoing page's Vue apps before Turbo swaps the body, so
  // their onUnmounted cleanup runs and their listeners/observers don't leak.
  // The incoming page's apps are (re)mounted on the following turbo:load.
  unmountPageApps();
  syncNavAuth(event.detail?.newBody);
});

// Update on resize
window.addEventListener("resize", setMobileClass);

// import ActiveStorage from '@rails/activestorage'
// ActiveStorage.start()

// Import all channels.
// const channels = import.meta.globEager('./**/*_channel.js')

// Example: Import a stylesheet in app/frontend/index.css
// import '~/index.css'

import { createApp } from "vue";
import NavRail from "../components/NavRail.vue";
import MobileTabBar from "../components/MobileTabBar.vue";
import { mountPageApp, unmountPageApps } from "./vueApps";

document.addEventListener("turbo:load", () => {
  // Mount Survey component
  const surveyElement = document.getElementById("user-survey");
  mountPageApp(surveyElement, () => import("../components/Survey.vue"));

  // Mount Results page component
  const resultsElement = document.getElementById("survey-results");
  if (resultsElement && !resultsElement.__vue_app__) {
    const data = resultsElement.dataset;
    mountPageApp(resultsElement, () => import("../components/results/ResultsPage.vue"), {
      surveySlug: data.surveySlug
    });
  }

  // Mount Results Hub component
  const resultsHubEl = document.getElementById("results-hub");
  mountPageApp(resultsHubEl, () => import("../components/results/ResultsHub.vue"));

  // Mount NavRail component (replaces Sidebar)
  const navEl = document.getElementById('nav-rail-wrapper')
  if (navEl && !navEl.__vue_app__) {
    createApp(NavRail, {
      navData: JSON.parse(navEl.dataset.nav),
    }).mount(navEl);
  }

  // Mount MobileTabBar component
  const tabBarEl = document.getElementById('mobile-tab-bar')
  if (tabBarEl && !tabBarEl.__vue_app__) {
    createApp(MobileTabBar, {
      navData: JSON.parse(tabBarEl.dataset.nav),
    }).mount(tabBarEl);
  }

  syncNavAuth(document);

  const faqEl = document.getElementById("faq-app");
  if (faqEl && !faqEl.__vue_app__) {
    const faqDataEl = document.getElementById("faq-data");
    let faqs = [];

    if (faqDataEl) {
      try {
        const parsedFaqs = JSON.parse(faqDataEl.textContent || "[]");
        faqs = Array.isArray(parsedFaqs) ? parsedFaqs : [];
      } catch (error) {
        console.error("Error parsing FAQ data:", error);
      }
    }

    mountPageApp(faqEl, () => import("../components/Faq.vue"), { faqs });
  }

  const friendsEl = document.getElementById("friends-app");
  mountPageApp(friendsEl, () => import("../components/Friends.vue"));

  // Mount Groups component
  const groupsEl = document.getElementById("groups-app");
  mountPageApp(groupsEl, () => import("../components/Groups.vue"), {
    userId: groupsEl?.dataset.userId,
  });

  // Mount Feed component
  const feedEl = document.getElementById("feed-app");
  mountPageApp(feedEl, () => import("../components/Feed.vue"));

  // Mount Social Home component
  const socialEl = document.getElementById("social-home-app");
  mountPageApp(socialEl, () => import("../components/SocialHome.vue"), {
    userId: socialEl?.dataset.userId,
  });

  // Mount marketing home — Compare friends bars
  const compareBarsEl = document.getElementById("compare-bars-app");
  mountPageApp(compareBarsEl, () => import("../components/marketing/CompareFriendsBars.vue"));

  // Mount marketing home — Taste test
  const tasteTestEl = document.getElementById("taste-test-app");
  mountPageApp(tasteTestEl, () => import("../components/marketing/TasteTest.vue"), {
    ctaPath: tasteTestEl?.dataset.ctaPath,
    ctaLabel: tasteTestEl?.dataset.ctaLabel,
  });

  // Mount marketing home — Hero chart
  const heroChartEl = document.getElementById("hero-chart-app");
  mountPageApp(heroChartEl, () => import("../components/marketing/HeroChart.vue"));
});
